/**
 * Cloud queue — offline storage and retry for failed uploads.
 *
 * Writes failed upload payloads to individual JSON files in .testrelic/queue/.
 * Flushes queued files on next successful auth, oldest first (FIFO).
 *
 * Constitution Principle VI: all I/O errors caught, never crash test run.
 * Constitution Principle VIII: atomic writes (tmp → rename).
 */

import {
  mkdirSync,
  writeFileSync,
  readFileSync,
  readdirSync,
  unlinkSync,
  renameSync,
  statSync,
} from 'node:fs';
import { join } from 'node:path';
import type { QueueEntry } from '@testrelic/core';
import { isValidQueueEntry } from '@testrelic/core';
import { prepareBody } from './cloud-upload.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUEUE_ENTRY_VERSION = '1.0.0';
const FLUSH_BATCH_SIZE = 10;

/** Best-effort message for a caught unknown, for queue-flush diagnostics. */
function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** A client error that will never succeed on replay, so the queued entry should be
 *  retired rather than retried forever. 408 (timeout) and 429 (rate-limit) ARE
 *  transient and are treated as retryable. */
function isPermanentHttpFailure(status: number): boolean {
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

// ---------------------------------------------------------------------------
// Write to Queue
// ---------------------------------------------------------------------------

/**
 * Write a failed upload payload to the offline queue.
 * Creates the queue directory if needed. Uses atomic write.
 * Silently fails on disk errors (ENOSPC, permissions).
 */
export function writeToQueue(
  queueDirectory: string,
  runId: string,
  type: string,
  reason: string,
  targetEndpoint: string,
  method: string,
  payload: Record<string, unknown>,
  headers: Record<string, string>,
): void {
  try {
    mkdirSync(queueDirectory, { recursive: true });

    const timestamp = Date.now();
    const filename = `${timestamp}-${runId}-${type}.json`;
    const filePath = join(queueDirectory, filename);

    const entry: QueueEntry = {
      version: QUEUE_ENTRY_VERSION,
      queuedAt: new Date(timestamp).toISOString(),
      reason,
      retryCount: 0,
      targetEndpoint,
      method,
      payload,
      headers,
    };

    const tmpPath = filePath + '.tmp';
    writeFileSync(tmpPath, JSON.stringify(entry, null, 2), 'utf-8');
    renameSync(tmpPath, filePath);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === 'ENOSPC') {
      process.stderr.write('⚠ TestRelic: Disk full — unable to queue upload data.\n');
    } else {
      process.stderr.write('⚠ TestRelic: Unable to queue upload data.\n');
    }
  }
}

// ---------------------------------------------------------------------------
// Queue Flush
// ---------------------------------------------------------------------------

/**
 * Flush queued uploads to the cloud. Oldest first (FIFO).
 * Stops on first failure (circuit-breaker).
 * Processes in batches of 10 for large queues.
 */
export async function flushQueue(
  queueDirectory: string,
  endpoint: string,
  accessToken: string,
): Promise<void> {
  let files: string[];
  try {
    files = readdirSync(queueDirectory)
      .filter((f) => f.endsWith('.json') && !f.endsWith('.tmp'))
      .sort(); // Lexicographic sort = FIFO by timestamp prefix
  } catch {
    return; // Queue directory doesn't exist or unreadable
  }

  if (files.length === 0) return;

  // Process in batches
  const batches: string[][] = [];
  for (let i = 0; i < files.length; i += FLUSH_BATCH_SIZE) {
    batches.push(files.slice(i, i + FLUSH_BATCH_SIZE));
  }

  for (const batch of batches) {
    for (const file of batch) {
      const filePath = join(queueDirectory, file);

      // Stage 1 — load + validate the entry. A bad/unreadable file is a PER-FILE
      // problem: delete it (with a warning) and move on, so one poison entry at the
      // FIFO head can't wedge every later entry behind it (cleanupExpiredQueue would
      // otherwise only clear it by age).
      let queueEntry: QueueEntry;
      try {
        const stat = statSync(filePath);
        if (!stat.isFile()) continue;
        const entry = JSON.parse(readFileSync(filePath, 'utf-8')) as unknown;
        if (!isValidQueueEntry(entry)) {
          process.stderr.write(`⚠ TestRelic: Corrupted queue file deleted: ${file}\n`);
          unlinkSync(filePath);
          continue;
        }
        queueEntry = entry as QueueEntry;
      } catch (err) {
        process.stderr.write(`⚠ TestRelic: Skipping unreadable queue file ${file}: ${errorMessage(err)}\n`);
        try { unlinkSync(filePath); } catch { /* ignore */ }
        continue;
      }

      // Stage 2 — replay the upload. A failure here is a SERVER/NETWORK problem, not a
      // per-file one, so it's handled distinctly: a permanent rejection retires the
      // entry; a transient one pauses the flush (with a log, so a stuck queue is
      // diagnosable instead of silently stalling).
      try {
        // Replay through the SAME prepareBody used by the direct upload path, so the
        // gzip/Content-Encoding (and the tiered 413 size-guard) stay in lockstep — a
        // payload gzipped or shed on first send must replay identically.
        const { body: flushBody, headers: prepared } = await prepareBody(queueEntry.payload);
        const flushHeaders: Record<string, string> = {
          ...queueEntry.headers,
          ...prepared,
          'Authorization': `Bearer ${accessToken}`,
        };
        const response = await fetch(queueEntry.targetEndpoint, {
          method: queueEntry.method,
          headers: flushHeaders,
          body: flushBody,
        });

        if (response.ok) {
          unlinkSync(filePath);
        } else if (isPermanentHttpFailure(response.status)) {
          // A permanent client error (400/401/403/413/…) will never succeed on replay —
          // move the entry aside so it can't wedge the queue forever.
          process.stderr.write(
            `⚠ TestRelic: Queue entry ${file} rejected (HTTP ${response.status}) — moving aside; it will not be retried.\n`,
          );
          try { renameSync(filePath, `${filePath}.failed`); }
          catch { try { unlinkSync(filePath); } catch { /* ignore */ } }
        } else {
          // Transient server error (5xx / 408 / 429) — stop and retry the whole queue
          // on the next flush; don't hammer a down server.
          process.stderr.write(
            `⚠ TestRelic: Queue flush paused — server returned HTTP ${response.status} for ${file}; will retry later.\n`,
          );
          return;
        }
      } catch (err) {
        // Network failure / fetch rejection — server unreachable. Stop and retry the
        // whole queue next flush; log so a persistently stuck queue is visible.
        process.stderr.write(`⚠ TestRelic: Queue flush paused — ${errorMessage(err)}; will retry later.\n`);
        return;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Retention Cleanup
// ---------------------------------------------------------------------------

/**
 * Delete queue files older than maxAge (in milliseconds).
 * Runs before flush on startup. Also ages out `.failed` entries (permanently-rejected
 * uploads moved aside by flushQueue) so they don't accumulate on disk forever — they
 * carry the same `queuedAt`, so the same retention applies.
 */
export function cleanupExpiredQueue(
  queueDirectory: string,
  maxAge: number,
): void {
  try {
    const files = readdirSync(queueDirectory)
      .filter((f) => (f.endsWith('.json') || f.endsWith('.failed')) && !f.endsWith('.tmp'));

    const now = Date.now();

    for (const file of files) {
      const filePath = join(queueDirectory, file);
      try {
        const raw = readFileSync(filePath, 'utf-8');
        const entry = JSON.parse(raw) as Record<string, unknown>;
        const queuedAt = entry.queuedAt;

        if (typeof queuedAt === 'string') {
          const queuedTime = new Date(queuedAt).getTime();
          if (now - queuedTime > maxAge) {
            unlinkSync(filePath);
          }
        }
      } catch {
        // Corrupted file — delete
        try { unlinkSync(filePath); } catch { /* ignore */ }
      }
    }
  } catch {
    // Queue directory doesn't exist — nothing to clean
  }
}
